import React, { useEffect, useState } from "react";
import { confirmable } from "react-confirm";
import { Modal, Row, Col, Tag, Select, Input, Spin, message, Button } from "antd";
import {
  apiGetBuyerApplicationDetail,
  apiApproveBuyerApplication,
  apiRejectBuyerApplication,
  apiUpdateBuyer,
} from "services/shopBuyerService";
import { STATUS_COLORS, STATUS_LABELS } from "../buyerUtils";

const { TextArea } = Input;

const Line = ({ label, children }) =>
  children ? (
    <div style={{ marginBottom: 6 }}>
      <span style={{ color: "var(--darkGreyColor)", marginRight: 8 }}>{label}:</span>
      <span>{children}</span>
    </div>
  ) : null;

function BuyerDetailDialog(props) {
  const { show, proceed, buyer, priceTiers } = props;
  const [detail, setDetail] = useState(buyer || {});
  const [isBusy, setIsBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [priceTierId, setPriceTierId] = useState(buyer?.price_tier_id || null);
  const [paymentTerms, setPaymentTerms] = useState(buyer?.payment_terms || "");
  const [adminNotes, setAdminNotes] = useState(buyer?.admin_notes || "");

  useEffect(() => {
    if (!buyer?.id) return;
    setIsBusy(true);
    apiGetBuyerApplicationDetail(buyer.id)
      .then((data) => {
        setIsBusy(false);
        setDetail(data);
        setPriceTierId(data.price_tier_id || null);
        setPaymentTerms(data.payment_terms || "");
        setAdminNotes(data.admin_notes || "");
      })
      .catch((err) => {
        setIsBusy(false);
        message.error(err);
      });
  }, [buyer?.id]);

  const billing = [detail.billing_address_line, detail.billing_city, detail.billing_state, detail.billing_postal_code, detail.billing_country]
    .filter((x) => x && String(x).trim() !== "")
    .join(", ");
  const delivery = [detail.delivery_address_line, detail.delivery_city, detail.delivery_state, detail.delivery_postal_code, detail.delivery_country]
    .filter((x) => x && String(x).trim() !== "")
    .join(", ");

  const onApprove = () => {
    setSaving(true);
    apiApproveBuyerApplication(detail.id, priceTierId, paymentTerms, adminNotes)
      .then((data) => {
        setSaving(false);
        message.success("Application approved. A welcome email has been sent.");
        proceed(data);
      })
      .catch((err) => {
        setSaving(false);
        message.error(err);
      });
  };

  const onReject = () => {
    setSaving(true);
    apiRejectBuyerApplication(detail.id, adminNotes)
      .then((data) => {
        setSaving(false);
        message.success("Application rejected.");
        proceed(data);
      })
      .catch((err) => {
        setSaving(false);
        message.error(err);
      });
  };

  const onSave = () => {
    setSaving(true);
    apiUpdateBuyer(detail.id, priceTierId, paymentTerms, adminNotes)
      .then((data) => {
        setSaving(false);
        message.success("Saved.");
        proceed(data);
      })
      .catch((err) => {
        setSaving(false);
        message.error(err);
      });
  };

  const footer =
    detail.status === "pending"
      ? [
          <Button key="cancel" onClick={() => proceed(null)}>Cancel</Button>,
          <Button key="reject" danger loading={saving} onClick={onReject}>Reject</Button>,
          <Button key="approve" type="primary" loading={saving} onClick={onApprove}>Approve application</Button>,
        ]
      : [
          <Button key="cancel" onClick={() => proceed(null)}>Cancel</Button>,
          <Button key="save" type="primary" loading={saving} onClick={onSave}>Save changes</Button>,
        ];

  return (
    <Modal
      open={show}
      width={780}
      destroyOnClose={true}
      maskClosable={false}
      title={`${detail.company_name || ""} — Wholesale application`}
      onCancel={() => proceed(null)}
      footer={footer}
    >
      <Spin spinning={isBusy}>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <h4>Company</h4>
            <Line label="Contact">{detail.contact_person}</Line>
            <Line label="Email">{detail.email}</Line>
            <Line label="Phone">{detail.phone}</Line>
            <Line label="Business type">{detail.business_type}</Line>
            <Line label="Website">{detail.website}</Line>
            <Line label="Tax / VAT no.">{detail.tax_vat_number}</Line>
            <Line label="Company reg. no.">{detail.company_reg_number}</Line>
          </Col>
          <Col xs={24} md={12}>
            <h4>Addresses</h4>
            <Line label="Billing">{billing}</Line>
            <Line label="Delivery">{delivery}</Line>
            <div style={{ marginTop: 12 }}>
              <span style={{ color: "var(--darkGreyColor)", marginRight: 8 }}>Status:</span>
              <Tag color={STATUS_COLORS[detail.status] || "default"}>{STATUS_LABELS[detail.status] || detail.status}</Tag>
            </div>
            <Line label="Orders placed">{detail.order_count}</Line>
          </Col>
        </Row>

        <Row gutter={24} style={{ marginTop: 16 }}>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 4, color: "var(--darkGreyColor)" }}>Price tier / customer group</label>
              <Select
                style={{ width: "100%" }}
                allowClear
                placeholder="No tier assigned"
                value={priceTierId || undefined}
                onChange={(v) => setPriceTierId(v || null)}
                options={(priceTiers || []).map((t) => ({ value: t.id, label: `${t.name} (${t.discount_percent}% off)` }))}
              />
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 4, color: "var(--darkGreyColor)" }}>Payment terms</label>
              <Input placeholder="e.g. Net 30, Payment on order" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} />
            </div>
          </Col>
        </Row>
        <div>
          <label style={{ display: "block", marginBottom: 4, color: "var(--darkGreyColor)" }}>Internal notes</label>
          <TextArea rows={3} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Not visible to the buyer" />
        </div>
      </Spin>
    </Modal>
  );
}

export default confirmable(BuyerDetailDialog);
